// Cross-window screen-slot coordination for the B1/B2 multi-window split.
//
// `screenId` ('1' = B1, '2' = B2) lives in sessionStorage (per browser window) and
// drives an independent layoutKey, so two windows can show fully independent
// WerkING-Partner views. A brand-new window has *empty* sessionStorage and therefore
// always fell back to '1' — so a second window collided on B1 instead of taking the
// free B2 slot (the "Default-Kollision" follow-up of commit 5fccae23c).
//
// This module is the missing coordinator. Each open window publishes a heartbeat'd
// claim on its current screen in localStorage (shared across same-origin windows),
// so a new window can synchronously read which screens are held by *live* windows and
// pick the lowest FREE one.
//
// Design — liveness, not locking:
//   * Claims are presence beacons with a TTL, not mutexes. A window that crashes or
//     is closed without firing `pagehide` simply goes stale and is pruned on the next
//     read, freeing its screen. No lock can leak.
//   * We never block or throw on "all screens taken": a 3rd window legitimately shares
//     B1. That path is logged (console.info), never silent.
//   * We DO fail loud on a corrupt registry: bad JSON in localStorage would otherwise
//     poison every window's startup pick, so we reset it with a console.error rather
//     than swallowing it. The registry is regenerable presence cache — safe to drop.
//
// Known limitation: localStorage has no atomic compare-and-set, so two windows opened
// in the exact same tick can both read "B1 free" and both pick B1. This degrades to
// the pre-fix behaviour (no worse) and does not occur for the real flow (user opens a
// second window seconds after the first).

export const SCREENS = ['1', '2'] as const;
export type ScreenId = (typeof SCREENS)[number];

/** Republish interval. Other windows treat a claim as live until STALE_MS without a beat. */
export const HEARTBEAT_MS = 3000;
/** A claim older than this is considered dead and pruned (> 2× heartbeat: tolerate one missed beat). */
const STALE_MS = 8000;

const CLAIMS_KEY = 'cui-screen-claims';      // localStorage: shared cross-window registry
const SCREEN_KEY = 'cui-screen-id';          // sessionStorage: this window's sticky choice
const CLAIM_ID_KEY = 'cui-screen-claim-id';  // sessionStorage: this window's stable claim id

interface Claim {
  screen: ScreenId;
  ts: number;
}

export function isScreenId(value: unknown): value is ScreenId {
  return typeof value === 'string' && (SCREENS as readonly string[]).includes(value);
}

/** Random id that works in secure AND non-secure contexts (crypto.randomUUID is HTTPS-only). */
function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buf = new Uint32Array(4);
    crypto.getRandomValues(buf);
    return Array.from(buf, (n) => n.toString(16)).join('-');
  }
  throw new Error('[screenClaim] no crypto source available to generate a claim id');
}

/** This window's stable claim id — survives reloads of the same window via sessionStorage. */
function getClaimId(): string {
  try {
    let id = sessionStorage.getItem(CLAIM_ID_KEY);
    if (!id) {
      id = genId();
      sessionStorage.setItem(CLAIM_ID_KEY, id);
    }
    return id;
  } catch {
    // sessionStorage unavailable (private mode / sandbox) — fall back to a per-page id.
    // The claim still works for this page's lifetime; it just won't survive a reload.
    return genId();
  }
}

/** Read the registry, validating shape and pruning stale claims. Resets loudly on corruption. */
function readClaims(): Record<string, Claim> {
  let raw: string | null;
  try {
    raw = localStorage.getItem(CLAIMS_KEY);
  } catch (err) {
    console.warn('[screenClaim] localStorage read failed:', err);
    return {};
  }
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    console.error('[screenClaim] corrupt claims registry — resetting:', err);
    try { localStorage.removeItem(CLAIMS_KEY); } catch { /* ignore */ }
    return {};
  }
  if (parsed === null || typeof parsed !== 'object') {
    console.error('[screenClaim] claims registry is not an object — resetting');
    try { localStorage.removeItem(CLAIMS_KEY); } catch { /* ignore */ }
    return {};
  }

  const now = Date.now();
  const live: Record<string, Claim> = {};
  for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (value === null || typeof value !== 'object') continue;
    const screen = (value as { screen?: unknown }).screen;
    const ts = (value as { ts?: unknown }).ts;
    if (!isScreenId(screen) || typeof ts !== 'number') continue;
    if (now - ts > STALE_MS) continue; // stale → drop, which frees its screen
    live[id] = { screen, ts };
  }
  return live;
}

function writeClaims(claims: Record<string, Claim>): void {
  try {
    localStorage.setItem(CLAIMS_KEY, JSON.stringify(claims));
  } catch (err) {
    console.warn('[screenClaim] localStorage write failed:', err);
  }
}

function readSticky(): ScreenId | null {
  try {
    const value = sessionStorage.getItem(SCREEN_KEY);
    return isScreenId(value) ? value : null;
  } catch {
    return null;
  }
}

function writeSticky(screen: ScreenId): void {
  try {
    sessionStorage.setItem(SCREEN_KEY, screen);
  } catch {
    /* ignore — non-sticky window, still functional for this page life */
  }
}

/** Upsert this window's claim with a fresh timestamp, preserving every other window's claim. */
export function publishScreenClaim(screen: ScreenId): void {
  const claims = readClaims();
  claims[getClaimId()] = { screen, ts: Date.now() };
  writeClaims(claims);
}

/** Remove this window's claim so the screen frees up for the next window. */
export function releaseScreenClaim(): void {
  const claims = readClaims();
  delete claims[getClaimId()];
  writeClaims(claims);
}

/** Persist a manual B1/B2 switch: make it this window's sticky choice and sync the registry. */
export function persistScreenChoice(screen: ScreenId): void {
  writeSticky(screen);
  publishScreenClaim(screen);
}

/**
 * Decide which screen this window should use, and claim it.
 *  - A window with a sticky choice (reload of an existing window) keeps it.
 *  - A brand-new window picks the lowest screen not held by another live window.
 *  - If every screen is held, it shares B1 (logged, never silent).
 */
export function acquireScreenId(): ScreenId {
  const sticky = readSticky();
  if (sticky) {
    publishScreenClaim(sticky);
    return sticky;
  }

  const myId = getClaimId();
  const claims = readClaims();
  const taken = new Set<ScreenId>();
  for (const [id, claim] of Object.entries(claims)) {
    if (id !== myId) taken.add(claim.screen);
  }

  const free = SCREENS.find((screen) => !taken.has(screen));
  const chosen: ScreenId = free ?? '1';
  if (!free) {
    console.info('[screenClaim] all screens held by live windows — new window shares B1');
  }

  persistScreenChoice(chosen);
  return chosen;
}

/**
 * Ein per window.open geoeffnetes Fenster erbt die sessionStorage seines Oeffners —
 * und damit dessen Claim-Kennung. Beide Fenster schrieben dann in denselben Eintrag
 * der Registry und verdraengten sich gegenseitig. Das Buehnen-Vollbild (zweiter
 * Bildschirm) ruft das deshalb einmal beim ersten Start, bevor es seinen Claim setzt.
 */
export function forgetInheritedClaim(): void {
  try {
    sessionStorage.removeItem(CLAIM_ID_KEY);
    sessionStorage.removeItem(SCREEN_KEY);
  } catch {
    /* ohne sessionStorage gibt es nichts Geerbtes */
  }
}
